import {cloneElement, type ReactElement} from "react";
import type {ShapeConfig} from "konva/lib/Shape";
import {Arc, Circle, Group, Rect} from "react-konva";
import type Combatant from "@/encounter/model/Combatant.ts";
import type {AreaEffect} from "@/encounter/model/AreaEffect.ts";
import {useCheckeredPattern} from "@/encounter/effect/AreaEffectHooks.ts";
import {PIXELS_PER_METER} from "@/dungeons/geometry/Coordinates.ts";

/** Passive artwork: no selection, history, viewport or mutation contexts. */
export default function AreaEffectAppearance({effect, combatant, selected = false, playerView = false}: {
    effect: AreaEffect; combatant?: Combatant; selected?: boolean; playerView?: boolean;
}) {
    const pattern = useCheckeredPattern(effect.color);
    if (playerView && effect.hidden) return null;
    const position = combatant?.position ?? effect.position;
    const rotation = combatant?.rotation ?? effect.rotation;
    let shape: ReactElement<ShapeConfig>;
    switch (effect.type) {
        case "Rect": shape = <Rect width={effect.width! * PIXELS_PER_METER} height={effect.length! * PIXELS_PER_METER}
            offsetX={effect.width! / 2 * PIXELS_PER_METER} offsetY={effect.length! * PIXELS_PER_METER} rotation={90}/>; break;
        case "Sphere": shape = <Circle radius={effect.radius! * PIXELS_PER_METER}/>; break;
        case "Arc": shape = <Arc innerRadius={Math.max(0, effect.radius! - effect.width!) * PIXELS_PER_METER}
            outerRadius={effect.radius! * PIXELS_PER_METER} angle={effect.angle!} rotation={-effect.angle! / 2}/>; break;
    }
    const fill = effect.hidden ? {fillPatternImage: pattern, fillPatternRepeat: "repeat"} : {fill: effect.color};
    return <Group x={position.x} y={position.y} rotation={rotation} listening={!playerView}>
        {cloneElement(shape, {...fill, opacity: effect.opacity})}
        {selected && !playerView && cloneElement(shape, {stroke: effect.color, strokeWidth: 3, opacity: 1, listening: false})}
        {selected && !playerView && <Circle radius={4} fill={effect.color} listening={false}/>}
    </Group>;
}
